import { SupabaseClient } from "@supabase/supabase-js";
import { getAvailableTimeSlots } from "./availability/slot-calculator";
import { fetchHostAvailability } from "./calendar/host-availability";
import { fetchHostAvailabilitySettings } from "./availability/enhanced-settings-service";
import { fetchWeeklyAvailabilityWindow } from "./availability/batch-weekly-availability-service";
import { getTimezoneAwareDayOfWeek, validateDayOfWeekMapping, debugDayOfWeekMapping } from "./timezone/day-of-week-utils";

// Enhanced caching with better performance characteristics
const dayAvailabilityCache = new Map<string, { 
  result: boolean; 
  timestamp: number; 
  promise?: Promise<boolean>;
  debug?: any;
}>();
const CACHE_DURATION = 5000; // 5 seconds to allow quick cache refresh during fixes
const MAX_CACHE_SIZE = 1000; // Prevent memory leaks

// LRU-style cache cleanup
const cleanupCache = () => {
  if (dayAvailabilityCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(dayAvailabilityCache.entries());
    // Remove oldest 25% of entries
    const toRemove = entries
      .sort((a, b) => a[1].timestamp - b[1].timestamp)
      .slice(0, Math.floor(entries.length * 0.25));
    
    toRemove.forEach(([key]) => dayAvailabilityCache.delete(key));
  }
};

/**
 * Enhanced logging for availability debugging
 */
function logAvailabilityStep(step: string, userId: string, date: Date, data: any) {
  // Only log in development to reduce console noise
  if (process.env.NODE_ENV === 'development') {
    console.log(`[isDayAvailable-${step}] ${userId} on ${date.toDateString()}:`, data);
  }
}

/**
 * Diagnose and repair weekly availability issues for a user
 */
async function diagnoseAndRepairAvailability(userId: string, supabaseClient: SupabaseClient): Promise<boolean> {
  try {
    console.log(`[isDayAvailable-Repair] Diagnosing availability issues for ${userId}`);
    
    // Call the database function to diagnose issues
    const { data: diagnosis, error: diagnosisError } = await supabaseClient
      .rpc('diagnose_weekly_availability', { p_user_id: userId });
    
    if (diagnosisError) {
      console.error(`[isDayAvailable-Repair] Diagnosis failed:`, diagnosisError);
      return false;
    }
    
    const userDiagnosis = diagnosis?.[0];
    if (!userDiagnosis || userDiagnosis.issue_summary === 'OK') {
      console.log(`[isDayAvailable-Repair] No issues found for ${userId}`);
      return true;
    }
    
    console.log(`[isDayAvailable-Repair] Issues found for ${userId}:`, userDiagnosis.issue_summary);
    
    // Repair the availability
    const { data: repairResult, error: repairError } = await supabaseClient
      .rpc('repair_weekly_availability', { p_user_id: userId });
    
    if (repairError) {
      console.error(`[isDayAvailable-Repair] Repair failed:`, repairError);
      return false;
    }
    
    console.log(`[isDayAvailable-Repair] Repair completed for ${userId}:`, repairResult);
    
    // Clear cache after repair
    const keysToDelete = Array.from(dayAvailabilityCache.keys())
      .filter(key => key.startsWith(`${userId}-`));
    keysToDelete.forEach(key => dayAvailabilityCache.delete(key));
    
    return true;
  } catch (err) {
    console.error(`[isDayAvailable-Repair] Exception during repair:`, err);
    return false;
  }
}

/**
 * Check if any time slots are available for a day using weekly availability and slot generation
 */
export async function isDayAvailable(
  userId: string, 
  date: Date,
  supabaseClient: SupabaseClient,
  organizerTimezone: string = 'UTC'
): Promise<boolean> {
  try {
    // Only log in development to reduce console noise
    if (process.env.NODE_ENV === 'development') {
      console.log(`[isDayAvailable] Checking availability for user ${userId} on ${date.toDateString()} in timezone ${organizerTimezone}`);
    }
    
    const cacheKey = `${userId}-${date.toDateString()}-${organizerTimezone}-v4`; // v4 to force cache refresh after timezone fix
    const now = Date.now();
    
    // Check cache first
    const cached = dayAvailabilityCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[isDayAvailable] Cache hit for ${cacheKey}: ${cached.result}`);
        logAvailabilityStep('Cache', userId, date, { result: cached.result, debug: cached.debug });
      }
      return cached.result;
    }
    
    // If there's already a pending request, return that promise
    if (cached?.promise) {
      return cached.promise;
    }
    
    // Create new request with comprehensive checking
    const requestPromise = (async () => {
      // Get timezone-aware day of week - THIS IS THE KEY FIX
      const dayOfWeek = getTimezoneAwareDayOfWeek(date, organizerTimezone);
      
      logAvailabilityStep('Start', userId, date, { 
        dayOfWeek, 
        organizerTimezone,
        utcDayOfWeek: date.getDay(),
        dateString: date.toDateString()
      });
      
      // Validate the day-of-week mapping
      validateDayOfWeekMapping(date, organizerTimezone);
      
      const debugInfo: any = {
        userId,
        date: date.toDateString(),
        dayOfWeek,
        organizerTimezone,
        utcDayOfWeek: date.getDay(),
        steps: []
      };
      
      // Step 1: Get weekly availability for this specific day using timezone-aware day
      let weeklyAvailability;
      
      try {
        weeklyAvailability = await fetchWeeklyAvailabilityWindow(userId, dayOfWeek);
        debugInfo.steps.push({
          step: 'weekly_availability_fetch',
          dayOfWeek,
          weeklyAvailability,
          success: true
        });
      } catch (err) {
        console.error("Error fetching weekly availability:", err);
        debugInfo.steps.push({
          step: 'weekly_availability_fetch',
          error: err,
          success: false
        });
        
        // Try to repair and retry once
        const repaired = await diagnoseAndRepairAvailability(userId, supabaseClient);
        if (repaired) {
          try {
            weeklyAvailability = await fetchWeeklyAvailabilityWindow(userId, dayOfWeek);
            debugInfo.steps.push({
              step: 'weekly_availability_retry_after_repair',
              weeklyAvailability,
              success: true
            });
          } catch (retryErr) {
            // Fallback to weekday default using timezone-aware day
            const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
            weeklyAvailability = {
              startTime: "09:00",
              endTime: "17:00", 
              isAvailable: isWeekday
            };
            debugInfo.steps.push({
              step: 'weekly_availability_fallback',
              weeklyAvailability,
              isWeekday
            });
          }
        } else {
          // Repair failed, use fallback
          const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
          weeklyAvailability = {
            startTime: "09:00",
            endTime: "17:00",
            isAvailable: isWeekday
          };
          debugInfo.steps.push({
            step: 'repair_failed_fallback',
            weeklyAvailability,
            isWeekday
          });
        }
      }
      
      // If the day is not available according to weekly settings, return false immediately
      if (!weeklyAvailability.isAvailable) {
        logAvailabilityStep('WeeklyUnavailable', userId, date, debugInfo);
        dayAvailabilityCache.set(cacheKey, { result: false, timestamp: now, debug: debugInfo });
        return false;
      }
      
      // Step 2: Get host settings for detailed checking
      let hostSettings;
      try {
        hostSettings = await fetchHostAvailabilitySettings(userId);
        debugInfo.steps.push({ step: 'host_settings', settings: hostSettings });
      } catch (err) {
        console.error("Error fetching host settings:", err);
        debugInfo.steps.push({ step: 'host_settings_error', error: err });
        hostSettings = {
          bufferBefore: 15,
          bufferAfter: 15,
          minNoticeTime: 60,
          timezone: organizerTimezone
        };
      }
      
      // Step 3: Check if user has a calendar connection before showing slots
      // Without a calendar connection, we cannot create bookings or check for conflicts
      const { data: calendarConnections, error: connectionError } = await supabaseClient
        .from('calendar_connections')
        .select('id')
        .eq('user_id', userId)
        .eq('is_primary', true)
        .limit(1);
      
      if (connectionError || !calendarConnections || calendarConnections.length === 0) {
        console.log(`[isDayAvailable] No calendar connection found for user ${userId}`);
        debugInfo.steps.push({ 
          step: 'calendar_connection_check', 
          hasConnection: false,
          error: connectionError
        });
        // No calendar connection means no slots should be available
        dayAvailabilityCache.set(cacheKey, { result: false, timestamp: now, debug: debugInfo });
        return false;
      }
      
      debugInfo.steps.push({ step: 'calendar_connection_check', hasConnection: true });
      
      // Step 4: Get calendar events for the day to check for conflicts
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      
      let busyEvents = [];
      try {
        const response = await fetchHostAvailability(
          userId,
          dayStart.toISOString(),
          dayEnd.toISOString(),
          organizerTimezone
        );
        busyEvents = response.items || [];
        debugInfo.steps.push({ 
          step: 'calendar_events', 
          eventCount: busyEvents.length,
          hasError: !!response.error 
        });
      } catch (err) {
        console.error("Error fetching calendar events:", err);
        debugInfo.steps.push({ step: 'calendar_events_error', error: err });
        // Continue without calendar events - don't fail completely
      }
      
      // Step 5: Use time slot generation with the actual weekly availability window
      try {
        const availabilityWindow = {
          startTime: weeklyAvailability.startTime,
          endTime: weeklyAvailability.endTime
        };
        
        const timeSlots = await getAvailableTimeSlots({
          date: date,
          events: busyEvents,
          settings: hostSettings as any,
          energyPreference: "any",
          duration: 30, // Standard duration for checking
          hostUserId: userId,
          timezone: organizerTimezone,
          includeUnavailable: false,
          availabilityWindow: availabilityWindow, // Use actual weekly availability
          wakeTime: 7
        });
        
        const hasAvailableSlots = timeSlots.length > 0;
        debugInfo.steps.push({
          step: 'time_slots_check',
          availabilityWindow,
          availableSlotCount: timeSlots.length,
          hasAvailableSlots,
          slots: timeSlots.slice(0, 3) // First few slots for debugging
        });
        
        logAvailabilityStep('TimeSlots', userId, date, {
          slotCount: timeSlots.length,
          hasSlots: hasAvailableSlots,
          availabilityWindow,
          dayOfWeekUsed: dayOfWeek,
          debugInfo
        });
        
        // Cache the result with debug info
        dayAvailabilityCache.set(cacheKey, { 
          result: hasAvailableSlots, 
          timestamp: now,
          debug: debugInfo
        });
        
        // Cleanup cache periodically
        if (Math.random() < 0.1) {
          cleanupCache();
        }
        
        return hasAvailableSlots;
      } catch (err) {
        console.error("Error generating time slots for day availability:", err);
        debugInfo.steps.push({ step: 'time_slots_error', error: err });
        
        // Fallback to weekly availability check
        logAvailabilityStep('FallbackToWeekly', userId, date, { hasWeeklyAvailability: weeklyAvailability.isAvailable, debugInfo });
        dayAvailabilityCache.set(cacheKey, { 
          result: weeklyAvailability.isAvailable, 
          timestamp: now,
          debug: debugInfo
        });
        return weeklyAvailability.isAvailable;
      }
    })();
    
    // Store the promise temporarily for deduplication
    dayAvailabilityCache.set(cacheKey, { 
      result: false, // Placeholder
      timestamp: now,
      promise: requestPromise
    });
    
    const result = await requestPromise;
    
    // Update cache with final result
    dayAvailabilityCache.set(cacheKey, { 
      result, 
      timestamp: now 
    });
    
    return result;
  } catch (err) {
    console.error("Error in isDayAvailable:", err);
    // On error, try a simple fallback check using timezone-aware day calculation
    const dayOfWeek = getTimezoneAwareDayOfWeek(date, organizerTimezone);
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    console.log(`[isDayAvailable-Fallback] Returning ${isWeekday} for ${userId} on ${date.toDateString()}, day ${dayOfWeek} in ${organizerTimezone}`);
    return isWeekday;
  }
}

/**
 * Legacy alias for backward compatibility - now supports timezone
 */
export const checkDayIsAvailable = (userId: string, date: Date, supabaseClient: SupabaseClient, timezone?: string) => 
  isDayAvailable(userId, date, supabaseClient, timezone);

/**
 * Batch availability check for multiple days with improved performance
 * Uses single weekly availability fetch instead of individual calls
 */
export async function batchCheckDayAvailability(
  userId: string,
  dates: Date[],
  supabaseClient: SupabaseClient,
  organizerTimezone: string = 'UTC'
): Promise<Map<string, boolean>> {
  const results = new Map<string, boolean>();
  const uncachedDates: Date[] = [];
  const now = Date.now();
  
  // Check cache for all dates first
  for (const date of dates) {
    const cacheKey = `${userId}-${date.toDateString()}-${organizerTimezone}-v4`;
    const cached = dayAvailabilityCache.get(cacheKey);
    
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      results.set(date.toDateString(), cached.result);
    } else {
      uncachedDates.push(date);
    }
  }
  
  if (uncachedDates.length === 0) {
    return results;
  }
  
  console.log(`[batchCheckDayAvailability] Processing ${uncachedDates.length} uncached dates for user ${userId}`);
  
  try {
    // Fetch weekly availability once for all days
    const { batchFetchWeeklyAvailability } = await import("./availability/batch-weekly-availability-service");
    const weeklyAvailabilityData = await batchFetchWeeklyAvailability(userId);
    
    // Get host settings once for all dates
    const hostSettings = await fetchHostAvailabilitySettings(userId);
    
    // Get date range for calendar events
    const startDate = new Date(Math.min(...uncachedDates.map(d => d.getTime())));
    const endDate = new Date(Math.max(...uncachedDates.map(d => d.getTime())));
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    // Fetch calendar events for the entire date range
    let busyEvents = [];
    try {
      const response = await fetchHostAvailability(
        userId,
        startDate.toISOString(),
        endDate.toISOString(),
        organizerTimezone
      );
      busyEvents = response.items || [];
    } catch (err) {
      console.error("Error fetching calendar events for batch:", err);
    }
    
    // Process each uncached date efficiently
    for (const date of uncachedDates) {
      try {
        const dayOfWeek = getTimezoneAwareDayOfWeek(date, organizerTimezone);
        const weeklyAvailability = weeklyAvailabilityData[dayOfWeek] || {
          startTime: "09:00",
          endTime: "17:00",
          isAvailable: dayOfWeek >= 1 && dayOfWeek <= 5
        };
        
        let isAvailable = false;
        
        if (weeklyAvailability.isAvailable) {
          // Filter events for this specific date
          const dayStart = new Date(date);
          dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(date);
          dayEnd.setHours(23, 59, 59, 999);
          
          const dayEvents = busyEvents.filter(event => {
            const eventStart = new Date(event.start?.dateTime || event.start?.date);
            return eventStart >= dayStart && eventStart <= dayEnd;
          });
          
          // Check for available time slots
          const timeSlots = await getAvailableTimeSlots({
            date: date,
            events: dayEvents,
            settings: hostSettings as any,
            energyPreference: "any",
            duration: 30,
            hostUserId: userId,
            timezone: organizerTimezone,
            includeUnavailable: false,
            availabilityWindow: {
              startTime: weeklyAvailability.startTime,
              endTime: weeklyAvailability.endTime
            },
            wakeTime: 7
          });
          
          isAvailable = timeSlots.length > 0;
        }
        
        results.set(date.toDateString(), isAvailable);
        
        // Cache the result
        const cacheKey = `${userId}-${date.toDateString()}-${organizerTimezone}-v4`;
        dayAvailabilityCache.set(cacheKey, { 
          result: isAvailable, 
          timestamp: now 
        });
        
      } catch (err) {
        console.error(`Error checking availability for ${date.toDateString()}:`, err);
        results.set(date.toDateString(), false); // Default to unavailable on error
      }
    }
    
    console.log(`[batchCheckDayAvailability] Completed batch processing with ${results.size} total results`);
    
  } catch (err) {
    console.error("Error in batch availability check:", err);
    // Fallback to individual checks
    for (const date of uncachedDates) {
      try {
        const isAvailable = await isDayAvailable(userId, date, supabaseClient, organizerTimezone);
        results.set(date.toDateString(), isAvailable);
      } catch (individualErr) {
        console.error(`Error checking individual availability for ${date.toDateString()}:`, individualErr);
        results.set(date.toDateString(), false);
      }
    }
  }
  
  return results;
}

/**
 * Gets the day of week from a date using timezone-aware calculation
 */
export function getDayOfWeek(date: Date, timezone?: string): number {
  if (timezone) {
    return getTimezoneAwareDayOfWeek(date, timezone);
  }
  return date.getDay();
}

/**
 * Interface for availability settings
 */
export interface AvailabilitySettings {
  bufferBefore: number;
  bufferAfter: number;
  minNoticeTime: number;
  startTime?: string;
  endTime?: string;
}

/**
 * Updates day availability in the database
 */
export async function updateDayAvailability(
  supabaseClient: any,
  userId: string,
  dayOfWeek: number,
  isAvailable: boolean,
  slots: any[]
): Promise<void> {
  try {
    // Pass slots directly as JSONB, not as a string
    const { data, error } = await supabaseClient.rpc(
      'update_day_availability',
      {
        p_user_id: userId,
        p_day_of_week: dayOfWeek,
        p_is_available: isAvailable,
        p_slots: slots
      }
    );
    
    if (error) {
      console.error('[updateDayAvailability] Error updating day availability:', error);
      throw error;
    }
    
    // Clear cache for this user and day
    const keysToDelete = Array.from(dayAvailabilityCache.keys())
      .filter(key => key.startsWith(`${userId}-`));
    keysToDelete.forEach(key => dayAvailabilityCache.delete(key));
    
  } catch (err) {
    console.error('[updateDayAvailability] Exception in updateDayAvailability function:', err);
    throw err;
  }
}

// Clear cache helper for testing/debugging
export function clearAvailabilityCache() {
  dayAvailabilityCache.clear();
  console.log('🧹 Availability cache cleared due to timezone fix');
}

// Force immediate cache clear for timezone fix
clearAvailabilityCache();

// Enhanced logging for calendar timezone debugging
console.log('🔧 [TIMEZONE-FIX] Availability cache cleared - calendar will use timezone-aware date creation at noon to prevent midnight boundary shifts');

/**
 * Enhanced error handling with detailed logging
 */
export function logAvailabilityDebug(userId: string, date: Date, result: boolean, source: string) {
  console.log(`[AvailabilityDebug] ${source}: User ${userId}, Date ${date.toDateString()}, Available: ${result}`);
}

/**
 * Enhanced debugging functions
 */
export function getAvailabilityCacheStats() {
  return {
    size: dayAvailabilityCache.size,
    entries: Array.from(dayAvailabilityCache.entries()).map(([key, value]) => ({
      key,
      result: value.result,
      timestamp: new Date(value.timestamp).toISOString(),
      hasDebug: !!value.debug
    }))
  };
}

// Simple memoization for health checks to prevent duplicates
const healthCheckCache = new Map<string, { result: any; timestamp: number }>();
const HEALTH_CHECK_CACHE_DURATION = 30000; // 30 seconds

// Health check function for availability system
export async function checkAvailabilitySystemHealth(
  userId: string,
  supabaseClient: SupabaseClient
): Promise<{
  status: 'healthy' | 'degraded' | 'unhealthy';
  issues: string[];
  recommendations: string[];
}> {
  const cacheKey = `health-${userId}`;
  const now = Date.now();
  
  // Check cache first
  const cached = healthCheckCache.get(cacheKey);
  if (cached && (now - cached.timestamp) < HEALTH_CHECK_CACHE_DURATION) {
    return cached.result;
  }
  const issues: string[] = [];
  const recommendations: string[] = [];
  
  try {
    // Check if user has weekly availability data
    const { data: weeklyData, error } = await supabaseClient
      .from('weekly_availability')
      .select('day_of_week, is_available')
      .eq('user_id', userId);
    
    if (error) {
      issues.push('Database connectivity issues');
      recommendations.push('Check database connection and permissions');
    } else if (!weeklyData || weeklyData.length === 0) {
      issues.push('No weekly availability data found');
      recommendations.push('Run availability repair function');
    } else {
      const availableDays = weeklyData.filter(d => d.is_available).length;
      if (availableDays === 0) {
        issues.push('No available days configured');
        recommendations.push('Configure at least one available day');
      }
      
      const missingDays = [];
      for (let i = 0; i <= 6; i++) {
        if (!weeklyData.find(d => d.day_of_week === i)) {
          missingDays.push(i);
        }
      }
      
      if (missingDays.length > 0) {
        issues.push(`Missing days of week: ${missingDays.join(', ')}`);
        recommendations.push('Run availability repair function to add missing days');
      }
    }
    
    // Test a sample date
    const testDate = new Date();
    const testResult = await isDayAvailable(userId, testDate, supabaseClient);
    
    if (typeof testResult !== 'boolean') {
      issues.push('isDayAvailable function not returning boolean');
      recommendations.push('Check availability logic implementation');
    }
    
  } catch (err) {
    issues.push(`System check failed: ${err}`);
    recommendations.push('Review system logs and error handling');
  }
  
  const status: 'healthy' | 'degraded' | 'unhealthy' = issues.length === 0 ? 'healthy' : 
                 issues.length <= 2 ? 'degraded' : 'unhealthy';
  
  const result = { status, issues, recommendations };
  
  // Cache the result
  healthCheckCache.set(cacheKey, { result, timestamp: now });
  
  return result;
}
